import { useState, useCallback, useRef } from "react";
import { API } from "@/api";
import axios from "axios";

const DEFAULTS = {
  verify_hours: "Can you confirm these hours are accurate?",
  confirm_offer: "Is this offer still available?",
  rate_offer: "How was your experience?",
  submit_business: "Know a spot we missed? Add it.",
  resolve_conflict: "Help us get the right info.",
  check_in: "Check in and earn points.",
  share_app: "Share this with a friend.",
  complete_profile: "Complete your profile for a better experience.",
  enable_notifications: "Turn on notifications to stay updated.",
  return_after_absence: "Welcome back!",
  confirm_halal: "Is this place halal? Your knowledge matters.",
  confirm_vegan: "Did you find vegan options here?",
  confirm_gluten_free: "Gluten free options available?",
  confirm_vegetarian: "Good vegetarian options here?",
  food_truck_spot: "Spotted a food truck? Share the location.",
  hidden_gem: "Found a hidden gem? Add it.",
  neighborhood_guardian: "Anything changed in your area?",
  offer_change_report: "Did this offer change? Let us know.",
  photo_verification: "Snap a photo and earn bonus points.",
  friends_invite: "Invite friends to plan together.",
};

/**
 * useCopyBank — Dynamic prompt rotation hook.
 *
 * Fetches random variations from the Copy Bank API.
 * Caches per session — no repeated API calls for the same key.
 * Falls back to sensible default if API unavailable.
 *
 * Usage:
 *   const { getText, getBatch } = useCopyBank();
 *   const prompt = getText("verify_hours");
 */
export function useCopyBank() {
  const cache = useRef({});
  const [, setTick] = useState(0);

  const getText = useCallback((key) => {
    // Return cached value if available
    if (cache.current[key]) return cache.current[key];

    // Start with default, then fetch async
    const fallback = DEFAULTS[key] || "";
    cache.current[key] = fallback;

    // Fire and forget — update on arrival
    axios
      .get(`${API}/copy-bank/${key}`)
      .then((res) => {
        const text = res.data?.text;
        if (text) {
          cache.current[key] = text;
          setTick((t) => t + 1); // trigger re-render
        }
      })
      .catch(() => {}); // fallback already set

    return fallback;
  }, []);

  const getBatch = useCallback(async (keys) => {
    // Return cached for any already fetched
    const uncached = keys.filter((k) => !cache.current[k]);
    if (uncached.length === 0) {
      return Object.fromEntries(keys.map((k) => [k, cache.current[k]]));
    }

    try {
      const res = await axios.get(`${API}/copy-bank/batch?keys=${uncached.join(",")}`);
      for (const [k, v] of Object.entries(res.data || {})) {
        if (v) cache.current[k] = v;
      }
    } catch {
      // Use defaults for uncached
      for (const k of uncached) {
        if (!cache.current[k]) cache.current[k] = DEFAULTS[k] || "";
      }
    }

    setTick((t) => t + 1);
    return Object.fromEntries(keys.map((k) => [k, cache.current[k] || DEFAULTS[k] || ""]));
  }, []);

  return { getText, getBatch };
}
